import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { DateRangeFilterConfig, DateRangeChangeEvent } from './date-range-filter.interface';

@Component({
  selector: 'app-date-range-filter',
  templateUrl: './date-range-filter.component.html',
  styleUrls: ['./date-range-filter.component.scss']
})
export class DateRangeFilterComponent implements OnInit, OnChanges {
  @Input() config: DateRangeFilterConfig = {
    initialDateRange: 'day',
    showTimePicker: true,
    allowFutureDates: false,
    offsetEndDateForApi: false,
    showFilterButton: true,
    filterButtonLabel: 'Filter',
    maxCustomRangeDays: 365
  };

  @Input() startDate: Date;
  @Input() endDate: Date;

  @Output() dateRangeChange = new EventEmitter<DateRangeChangeEvent>();
  @Output() filterClick = new EventEmitter<DateRangeChangeEvent>();

  dateRange: string = 'day';
  report_from_time: string | null = null;
  report_to_time: string | null = null;

  get prevButtonTooltip(): string {
    switch(this.dateRange) {
      case 'day':
        return 'Previous Day';
      case 'week':
        return 'Previous Week';
      case 'month':
        return 'Previous Month';
      default:
        return 'Previous';
    }
  }

  get nextButtonTooltip(): string {
    switch(this.dateRange) {
      case 'day':
        return 'Next Day';
      case 'week':
        return 'Next Week';
      case 'month':
        return 'Next Month';
      default:
        return 'Next';
    }
  }

  get isNextButtonDisabled(): boolean {
    if (this.config.allowFutureDates) {
      return false;
    }

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(23, 59, 59, 999);

    switch(this.dateRange) {
      case 'day':
        return this.wouldNextDayExceedTomorrow();
      case 'week':
        return this.wouldNextWeekExceedTomorrow();
      case 'month':
        return this.wouldNextMonthExceedTomorrow();
      default:
        return false;
    }
  }

  private wouldNextDayExceedTomorrow(): boolean {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const newEndDate = new Date(this.endDate);
    newEndDate.setDate(newEndDate.getDate() + 1);

    if (this.report_from_time && this.report_to_time) {
      const endHour = Number(this.report_to_time.split(':')[0]);
      const endMinute = Number(this.report_to_time.split(':')[1]);
      const startHour = Number(this.report_from_time.split(':')[0]);
      
      if (endHour < startHour || (endHour === startHour && endMinute <= Number(this.report_from_time.split(':')[1]))) {
        newEndDate.setHours(endHour, endMinute, 0, 0);
      } else {
        newEndDate.setHours(endHour, endMinute, 0, 0);
      }
    } else {
      newEndDate.setHours(23, 59, 59, 999);
    }

    return newEndDate.getTime() > tomorrow.getTime();
  }

  private wouldNextWeekExceedTomorrow(): boolean {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const newStartDate = new Date(this.startDate);
    const dayOfWeek = newStartDate.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    newStartDate.setDate(newStartDate.getDate() - daysToSubtract + 7);

    const newEndDate = new Date(newStartDate);
    newEndDate.setDate(newStartDate.getDate() + 6);

    if (this.report_from_time && this.report_to_time) {
      const endHour = Number(this.report_to_time.split(':')[0]);
      const endMinute = Number(this.report_to_time.split(':')[1]);
      const startHour = Number(this.report_from_time.split(':')[0]);
      
      if (endHour < startHour || (endHour === startHour && endMinute <= Number(this.report_from_time.split(':')[1]))) {
        newEndDate.setDate(newEndDate.getDate() + 1);
        newEndDate.setHours(endHour, endMinute, 0, 0);
      } else {
        newEndDate.setHours(endHour, endMinute, 0, 0);
      }
    } else {
      newEndDate.setHours(23, 59, 59, 999);
    }

    return newEndDate.getTime() > tomorrow.getTime();
  }

  private wouldNextMonthExceedTomorrow(): boolean {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    let newStartDate = new Date(this.startDate.getFullYear(), this.startDate.getMonth() + 1, 1);
    let newEndDate = new Date(newStartDate.getFullYear(), newStartDate.getMonth() + 1, 0);

    if (this.report_from_time && this.report_to_time) {
      const endHour = Number(this.report_to_time.split(':')[0]);
      const endMinute = Number(this.report_to_time.split(':')[1]);
      const startHour = Number(this.report_from_time.split(':')[0]);
      
      if (endHour < startHour || (endHour === startHour && endMinute <= Number(this.report_from_time.split(':')[1]))) {
        newEndDate.setDate(newEndDate.getDate() + 1);
        newEndDate.setHours(endHour, endMinute, 0, 0);
      } else {
        newEndDate.setHours(endHour, endMinute, 0, 0);
      }
    } else {
      newEndDate.setHours(23, 59, 59, 999);
    }

    return newEndDate.getTime() > tomorrow.getTime();
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private toastrService: NbToastrService
  ) {}

  ngOnInit(): void {
    this.dateRange = this.config.initialDateRange || 'day';
    this.initializeDates();
    if (!this.startDate || !this.endDate) {
      this.initializeDates();
    }
    this.loadTimeSettings();
    this.emitDateChange();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && !changes['config'].firstChange) {
      this.dateRange = this.config.initialDateRange || 'day';
    }
  }

  private initializeDates(): void {
    this.startDate = new Date();
    this.endDate = new Date();
    this.endDate.setDate(this.endDate.getDate() + 1);
    if (localStorage.getItem('report_from') && localStorage.getItem('report_to')) {
      const report_from = localStorage.getItem('report_from');
      const report_to = localStorage.getItem('report_to');
      this.report_from_time = report_from;
      this.report_to_time = report_to;
      this.startDate.setHours(Number(report_from.split(':')[0]), Number(report_from.split(':')[1]), 0, 0);
      this.endDate.setHours(Number(report_to.split(':')[0]), Number(report_to.split(':')[1]), 0, 0);
    } else {
      this.startDate.setHours(0, 0, 0, 0);
      this.endDate.setHours(23, 59, 59, 999);
    }
  }

  private loadTimeSettings(): void {
    if (localStorage.getItem('report_from') && localStorage.getItem('report_to')) {
      this.report_from_time = localStorage.getItem('report_from');
      this.report_to_time = localStorage.getItem('report_to');
    }
  }

  onDateRangeChange(): void {
    this.navigateToCurrentPeriod();
  }

  navigateToCurrentPeriod(): void {
    switch(this.dateRange) {
      case 'day':
        this.navigateToCurrentDay();
        break;
      case 'week':
        this.navigateToCurrentWeek();
        break;
      case 'month':
        this.navigateToCurrentMonth();
        break;
      case 'custom':
        this.applyTimeSettings();
        this.emitDateChange();
        break;
    }
  }

  navigateToCurrentDay(): void {
    const today = new Date();
    this.startDate = new Date(today);
    this.endDate = new Date(today);
    this.applyTimeSettings();
    this.emitDateChange();
    this.cdr.detectChanges();
  }

  navigateToCurrentWeek(): void {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    this.startDate = new Date(today);
    this.startDate.setDate(today.getDate() - daysToSubtract);

    this.endDate = new Date(this.startDate);
    this.endDate.setDate(this.startDate.getDate() + 6);

    this.applyTimeSettings();
    this.emitDateChange();
    this.cdr.detectChanges();
  }

  navigateToCurrentMonth(): void {
    const today = new Date();
    this.startDate = new Date(today.getFullYear(), today.getMonth(), 1);
    this.endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    this.applyTimeSettings();
    this.emitDateChange();
    this.cdr.detectChanges();
  }

  applyTimeSettings(): void {
    if (this.report_from_time && this.report_to_time) {
      const startHour = Number(this.report_from_time.split(':')[0]);
      const startMinute = Number(this.report_from_time.split(':')[1]);
      const endHour = Number(this.report_to_time.split(':')[0]);
      const endMinute = Number(this.report_to_time.split(':')[1]);

      this.startDate.setHours(startHour, startMinute, 0, 0);

      // Handle overnight business hours (e.g., 3 AM to 3 AM)
      if (endHour < startHour || (endHour === startHour && endMinute <= startMinute)) {
        // Business day ends the next day, so add 1 day to end date
        this.endDate.setDate(this.endDate.getDate() + 1);
        this.endDate.setHours(endHour, endMinute, 0, 0);
      } else {
        this.endDate.setHours(endHour, endMinute, 0, 0);
      }
    } else {
      // Default time settings
      this.startDate.setHours(0, 0, 0, 0);
      this.endDate.setHours(23, 59, 59, 999);
    }
  }

  goPrev(): void {
    switch(this.dateRange) {
      case 'day':
        this.prevDayDate();
        break;
      case 'week':
        this.previousWeek();
        break;
      case 'month':
        this.previousMonth();
        break;
    }
  }

  goNext(): void {
    switch(this.dateRange) {
      case 'day':
        this.nextDayDate();
        break;
      case 'week':
        this.nextWeek();
        break;
      case 'month':
        this.nextMonth();
        break;
    }
  }

  prevDayDate(): void {
    const newStartDate = new Date(this.startDate);
    newStartDate.setDate(newStartDate.getDate() - 1);
    const newEndDate = new Date(this.endDate);
    newEndDate.setDate(newEndDate.getDate() - 1);

    // Apply report time settings if available
    if (this.report_from_time && this.report_to_time) {
      const startHour = Number(this.report_from_time.split(':')[0]);
      const startMinute = Number(this.report_from_time.split(':')[1]);
      const endHour = Number(this.report_to_time.split(':')[0]);
      const endMinute = Number(this.report_to_time.split(':')[1]);

      newStartDate.setHours(startHour, startMinute, 0, 0);

      // Handle overnight business hours (e.g., 3 AM to 3 AM)
      if (endHour < startHour || (endHour === startHour && endMinute <= startMinute)) {
        // For overnight business hours, when going to previous day:
        // End date should be the same day as start date (not next day)
        newEndDate.setHours(endHour, endMinute, 0, 0);
      } else {
        newEndDate.setHours(endHour, endMinute, 0, 0);
      }
    } else {
      newStartDate.setHours(0, 0, 0, 0);
      newEndDate.setHours(23, 59, 59, 999);
    }

    this.startDate = newStartDate;
    this.endDate = newEndDate;
    this.emitDateChange();
    this.cdr.detectChanges();
  }

  nextDayDate(): void {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const newStartDate = new Date(this.startDate);
    newStartDate.setDate(newStartDate.getDate() + 1);
    const newEndDate = new Date(this.endDate);
    newEndDate.setDate(newEndDate.getDate() + 1);

    // Apply report time settings if available
    if (this.report_from_time && this.report_to_time) {
      const startHour = Number(this.report_from_time.split(':')[0]);
      const startMinute = Number(this.report_from_time.split(':')[1]);
      const endHour = Number(this.report_to_time.split(':')[0]);
      const endMinute = Number(this.report_to_time.split(':')[1]);

      newStartDate.setHours(startHour, startMinute, 0, 0);

      // Handle overnight business hours (e.g., 3 AM to 3 AM)
      if (endHour < startHour || (endHour === startHour && endMinute <= startMinute)) {
        // For overnight business hours, when going to next day:
        // End date should be the next day after start date (already incremented)
        newEndDate.setHours(endHour, endMinute, 0, 0);
      } else {
        newEndDate.setHours(endHour, endMinute, 0, 0);
      }
    } else {
      newStartDate.setHours(0, 0, 0, 0);
      newEndDate.setHours(23, 59, 59, 999);
    }

    if (!this.config.allowFutureDates && newEndDate.getTime() > tomorrow.getTime()) {
      // Button should be disabled, do nothing
      return;
    }

    this.startDate = newStartDate;
    this.endDate = newEndDate;
    this.emitDateChange();
    this.cdr.detectChanges();
  }

  previousWeek(): void {
    const newStartDate = new Date(this.startDate);
    const dayOfWeek = newStartDate.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    newStartDate.setDate(newStartDate.getDate() - daysToSubtract - 7);

    const newEndDate = new Date(newStartDate);
    newEndDate.setDate(newStartDate.getDate() + 6);

    // Apply report time settings if available
    if (this.report_from_time && this.report_to_time) {
      const startHour = Number(this.report_from_time.split(':')[0]);
      const startMinute = Number(this.report_from_time.split(':')[1]);
      const endHour = Number(this.report_to_time.split(':')[0]);
      const endMinute = Number(this.report_to_time.split(':')[1]);

      newStartDate.setHours(startHour, startMinute, 0, 0);

      // Handle overnight business hours (e.g., 3 AM to 3 AM)
      if (endHour < startHour || (endHour === startHour && endMinute <= startMinute)) {
        // Business day ends the next day, so add 1 day to end date
        newEndDate.setDate(newEndDate.getDate() + 1);
        newEndDate.setHours(endHour, endMinute, 0, 0);
      } else {
        newEndDate.setHours(endHour, endMinute, 0, 0);
      }
    } else {
      newStartDate.setHours(0, 0, 0, 0);
      newEndDate.setHours(23, 59, 59, 999);
    }

    this.startDate = newStartDate;
    this.endDate = newEndDate;
    this.emitDateChange();
    this.cdr.detectChanges();
  }

  nextWeek(): void {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const newStartDate = new Date(this.startDate);
    const dayOfWeek = newStartDate.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    newStartDate.setDate(newStartDate.getDate() - daysToSubtract + 7);

    const newEndDate = new Date(newStartDate);
    newEndDate.setDate(newStartDate.getDate() + 6);

    // Apply report time settings if available
    if (this.report_from_time && this.report_to_time) {
      const startHour = Number(this.report_from_time.split(':')[0]);
      const startMinute = Number(this.report_from_time.split(':')[1]);
      const endHour = Number(this.report_to_time.split(':')[0]);
      const endMinute = Number(this.report_to_time.split(':')[1]);

      newStartDate.setHours(startHour, startMinute, 0, 0);

      // Handle overnight business hours (e.g., 3 AM to 3 AM)
      if (endHour < startHour || (endHour === startHour && endMinute <= startMinute)) {
        // Business day ends the next day, so add 1 day to end date
        newEndDate.setDate(newEndDate.getDate() + 1);
        newEndDate.setHours(endHour, endMinute, 0, 0);
      } else {
        newEndDate.setHours(endHour, endMinute, 0, 0);
      }
    } else {
      newStartDate.setHours(0, 0, 0, 0);
      newEndDate.setHours(23, 59, 59, 999);
    }

    if (!this.config.allowFutureDates && newEndDate.getTime() > tomorrow.getTime()) {
      // Button should be disabled, do nothing
      return;
    }

    this.startDate = newStartDate;
    this.endDate = newEndDate;
    this.emitDateChange();
    this.cdr.detectChanges();
  }

  previousMonth(): void {
    const newStartDate = new Date(this.startDate.getFullYear(), this.startDate.getMonth() - 1, 1);
    const newEndDate = new Date(newStartDate.getFullYear(), newStartDate.getMonth() + 1, 0);

    // Apply report time settings if available
    if (this.report_from_time && this.report_to_time) {
      const startHour = Number(this.report_from_time.split(':')[0]);
      const startMinute = Number(this.report_from_time.split(':')[1]);
      const endHour = Number(this.report_to_time.split(':')[0]);
      const endMinute = Number(this.report_to_time.split(':')[1]);

      newStartDate.setHours(startHour, startMinute, 0, 0);

      // Handle overnight business hours (e.g., 3 AM to 3 AM)
      if (endHour < startHour || (endHour === startHour && endMinute <= startMinute)) {
        // Business day ends the next day, so add 1 day to end date
        newEndDate.setDate(newEndDate.getDate() + 1);
        newEndDate.setHours(endHour, endMinute, 0, 0);
      } else {
        newEndDate.setHours(endHour, endMinute, 0, 0);
      }
    } else {
      newStartDate.setHours(0, 0, 0, 0);
      newEndDate.setHours(23, 59, 59, 999);
    }

    this.startDate = newStartDate;
    this.endDate = newEndDate;
    this.emitDateChange();
    this.cdr.detectChanges();
  }

  nextMonth(): void {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    let newStartDate = new Date(this.startDate.getFullYear(), this.startDate.getMonth() + 1, 1);
    let newEndDate = new Date(newStartDate.getFullYear(), newStartDate.getMonth() + 1, 0);

    // Apply report time settings if available
    if (this.report_from_time && this.report_to_time) {
      const startHour = Number(this.report_from_time.split(':')[0]);
      const startMinute = Number(this.report_from_time.split(':')[1]);
      const endHour = Number(this.report_to_time.split(':')[0]);
      const endMinute = Number(this.report_to_time.split(':')[1]);

      newStartDate.setHours(startHour, startMinute, 0, 0);

      // Handle overnight business hours (e.g., 3 AM to 3 AM)
      if (endHour < startHour || (endHour === startHour && endMinute <= startMinute)) {
        // Business day ends the next day, so add 1 day to end date
        newEndDate.setDate(newEndDate.getDate() + 1);
        newEndDate.setHours(endHour, endMinute, 0, 0);
      } else {
        newEndDate.setHours(endHour, endMinute, 0, 0);
      }
    } else {
      newStartDate.setHours(0, 0, 0, 0);
      newEndDate.setHours(23, 59, 59, 999);
    }

    if (!this.config.allowFutureDates) {
      // Check if the new end date exceeds tomorrow
      if (newEndDate.getTime() > tomorrow.getTime()) {
        newEndDate = new Date(tomorrow);
        newEndDate.setHours(23, 59, 59, 999);
      }

      // Ensure the start date does not exceed the current date
      if (newStartDate.getTime() > tomorrow.getTime()) {
        newStartDate = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), 1);
        newStartDate.setHours(0, 0, 0, 0);
      }

      // Additional validation
      if (newStartDate.getTime() > newEndDate.getTime()) {
        this.toastrService.warning('Start Date should be less than end Date', 'Warning');
        return;
      }
    }

    this.startDate = newStartDate;
    this.endDate = newEndDate;
    this.emitDateChange();
    this.cdr.detectChanges();
  }

  onStartDateChange(newDate: Date): void {
    if (!newDate) return;
    this.startDate = newDate;
    // Validate and emit change for custom date range
    if (this.dateRange === 'custom') {
      // Validation will be done in emitDateChange
      this.emitDateChange();
    }
  }

  onEndDateChange(newDate: Date): void {
    if (!newDate) return;
    this.endDate = newDate;
    // Validate and emit change for custom date range
    if (this.dateRange === 'custom') {
      // Validation will be done in emitDateChange
      this.emitDateChange();
    }
  }

  onFilterClick(): void {
    if (!this.startDate || !this.endDate) {
      this.toastrService.warning('Date fields required', 'Warning');
      return;
    }

    if (this.startDate.getTime() > this.endDate.getTime()) {
      this.toastrService.warning('Start Date should be less than end Date', 'Warning');
      return;
    }

    this.applyTimeSettings();
    
    // Validate custom date range before emitting
    if (!this.validateAndAdjustCustomDateRange()) {
      // Date was adjusted, re-apply time settings and emit
      this.applyTimeSettings();
    }
    
    this.emitDateChange(true);
  }

  private calculateDaysDifference(startDate: Date, endDate: Date): number {
    const timeDiff = endDate.getTime() - startDate.getTime();
    return Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
  }

  private validateAndAdjustCustomDateRange(): boolean {
    // Only validate for custom date range
    if (this.dateRange !== 'custom') {
      return true;
    }

    const maxDays = this.config.maxCustomRangeDays || 365;
    const daysDifference = this.calculateDaysDifference(this.startDate, this.endDate);

    if (daysDifference > maxDays) {
      // Show alert (only if not already adjusted)
      const currentEndDateStr = this.endDate.toISOString();
      
      // Auto-adjust end date to maximum allowed range
      const adjustedEndDate = new Date(this.startDate);
      adjustedEndDate.setDate(adjustedEndDate.getDate() + maxDays);
      
      // Apply time settings if available
      if (this.report_from_time && this.report_to_time) {
        const endHour = Number(this.report_to_time.split(':')[0]);
        const endMinute = Number(this.report_to_time.split(':')[1]);
        adjustedEndDate.setHours(endHour, endMinute, 59, 999);
      } else {
        adjustedEndDate.setHours(23, 59, 59, 999);
      }

      // Only show alert if the end date is actually different (user changed it manually)
      if (currentEndDateStr !== adjustedEndDate.toISOString()) {
        this.toastrService.warning(
          `Maximum range allowed exceeded. Maximum allowed is ${maxDays} days. End date has been adjusted.`,
          'Date Range Limit Exceeded'
        );
      }

      this.endDate = adjustedEndDate;
      this.cdr.detectChanges();
      return false;
    }

    return true;
  }

  private emitDateChange(isFilterClick: boolean = false): void {
    // Validate custom date range before emitting
    if (!this.validateAndAdjustCustomDateRange()) {
      // If validation failed and date was adjusted, emit the adjusted date
      // but don't proceed with filter click if it was a filter click
      if (!isFilterClick) {
        this.dateRangeChange.emit({
          startDate: new Date(this.startDate),
          endDate: new Date(this.endDate)
        });
      }
      return;
    }

    const event: DateRangeChangeEvent = {
      startDate: new Date(this.startDate),
      endDate: new Date(this.endDate)
    };

    if (this.config.offsetEndDateForApi) {
      const apiEndDate = new Date(this.endDate);
      apiEndDate.setDate(apiEndDate.getDate() + 1);
      event.apiStartDate = new Date(this.startDate);
      event.apiEndDate = apiEndDate;
    }

    if (isFilterClick) {
      this.filterClick.emit(event);
    } else {
      this.dateRangeChange.emit(event);
      // Auto-filter only for non-custom date ranges
      if (this.dateRange !== 'custom') {
        this.filterClick.emit(event);
      }
    }
  }
}

